import { useState, useEffect } from "react";
import { Send, Loader2, Megaphone, Bell, Save } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { AdminHeader, Field } from "@/components/admin/ui";

const TARGETS = [
  { value: "all", label: "All Users" },
  { value: "role:customer", label: "Customers only" },
  { value: "role:staff", label: "Staff only" },
];
const TYPES = [
  { value: "promotion", label: "Promotion" },
  { value: "system", label: "System Announcement" },
  { value: "tournament", label: "Tournament" },
  { value: "points", label: "Points & Rewards" },
  { value: "booking", label: "Booking" },
];
const PRIORITIES = ["low", "medium", "high", "urgent"];

export default function AdminNotifications() {
  const [form, setForm] = useState({ title: "", message: "", target_users: "all", type: "promotion", priority: "medium" });
  const [sending, setSending] = useState(false);
  const [surpass, setSurpass] = useState(null);
  const [savingSurpass, setSavingSurpass] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/surpass-settings").then(({ data }) => setSurpass(data)).catch(() => {});
  }, []);

  const saveSurpass = async () => {
    setSavingSurpass(true);
    try {
      const { data } = await apiClient.put("/admin/surpass-settings", surpass);
      setSurpass(data);
      toast.success("Surpass settings saved");
    } catch (err) { toast.error(getErrorMessage(err, "Save failed")); } finally { setSavingSurpass(false); }
  };

  const send = async () => {
    if (!form.title || !form.message) { toast.error("Title and message are required"); return; }
    setSending(true);
    try {
      const { data } = await apiClient.post("/admin/notifications/send", form);
      toast.success(data.message || "Notification sent");
      setForm({ ...form, title: "", message: "" });
    } catch (err) { toast.error(getErrorMessage(err, "Send failed")); } finally { setSending(false); }
  };

  return (
    <div>
      <AdminHeader title="Notifications" subtitle="Broadcast an announcement to your players." />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass rounded-2xl p-6">
          <Field label="Title"><input className="input-cyber" placeholder="e.g. Weekend Double Points!" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="notif-title" /></Field>
          <Field label="Message"><textarea className="input-cyber min-h-[120px] resize-none" placeholder="Write your announcement..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="notif-message" /></Field>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Audience">
              <select className="input-cyber" value={form.target_users} onChange={(e) => setForm({ ...form, target_users: e.target.value })} data-testid="notif-target">
                {TARGETS.map((t) => <option key={t.value} value={t.value} className="bg-slate-900">{t.label}</option>)}
              </select>
            </Field>
            <Field label="Type">
              <select className="input-cyber" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="notif-type">
                {TYPES.map((t) => <option key={t.value} value={t.value} className="bg-slate-900">{t.label}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select className="input-cyber capitalize" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="notif-priority">
                {PRIORITIES.map((p) => <option key={p} value={p} className="bg-slate-900">{p}</option>)}
              </select>
            </Field>
          </div>
          <button onClick={send} disabled={sending} className="neon-btn px-6 py-3 mt-4" data-testid="notif-send">
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send Notification</>}
          </button>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(255,46,151,0.15)", border: "1px solid rgba(255,46,151,0.4)" }}>
            <Megaphone className="w-6 h-6" style={{ color: "#ff2e97" }} />
          </div>
          <h3 className="font-display font-bold text-white mb-2">Live Preview</h3>
          <div className="rounded-xl p-4" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)" }}>
            <p className="text-white font-semibold">{form.title || "Notification title"}</p>
            <p className="text-slate-400 text-sm mt-1">{form.message || "Your message preview appears here."}</p>
            <p className="text-slate-600 text-xs mt-3 capitalize">{form.priority} priority · {TARGETS.find((t) => t.value === form.target_users)?.label}</p>
          </div>
        </div>
      </div>

      {surpass && (
        <div className="glass rounded-2xl p-6 mt-6" data-testid="surpass-settings-card">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,229,255,0.15)", border: "1px solid rgba(0,229,255,0.4)" }}>
              <Bell className="w-5 h-5" style={{ color: "#00e5ff" }} />
            </div>
            <h3 className="font-display text-lg font-bold text-white">&quot;Someone took your spot&quot; notifications</h3>
          </div>
          <p className="text-slate-400 text-sm mb-4">When a driver is overtaken on any leaderboard (lap times, car clubs, most driven/flown/VR), nudge them to book and reclaim their spot.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid="surpass-enabled">
              <input type="checkbox" checked={surpass.enabled} onChange={(e) => setSurpass({ ...surpass, enabled: e.target.checked })} className="accent-cyan-400" />
              Enable surpass notifications
            </label>
            <Field label="Delivery">
              <select className="input-cyber" value={surpass.mode} onChange={(e) => setSurpass({ ...surpass, mode: e.target.value })} data-testid="surpass-mode">
                <option value="batch" className="bg-slate-900">Batch (combine boards into one)</option>
                <option value="individual" className="bg-slate-900">Individual (one per board)</option>
              </select>
            </Field>
            <Field label="Frequency (per board)">
              <select className="input-cyber" value={surpass.frequency_hours} onChange={(e) => setSurpass({ ...surpass, frequency_hours: Number(e.target.value) })} data-testid="surpass-frequency">
                <option value={24} className="bg-slate-900">Once per day</option>
                <option value={48} className="bg-slate-900">Every 2 days</option>
                <option value={72} className="bg-slate-900">Every 3 days</option>
                <option value={168} className="bg-slate-900">Once per week</option>
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid="surpass-reping-enabled">
              <input type="checkbox" checked={surpass.reping_enabled} onChange={(e) => setSurpass({ ...surpass, reping_enabled: e.target.checked })} className="accent-cyan-400" />
              Re-ping if they haven&apos;t booked
            </label>
            <Field label="Re-ping every (days, 0 = off)">
              <input type="number" min="0" max="30" className="input-cyber" value={surpass.reping_days} onChange={(e) => setSurpass({ ...surpass, reping_days: Number(e.target.value) })} data-testid="surpass-reping-days" />
            </Field>
          </div>
          <button onClick={saveSurpass} disabled={savingSurpass} className="neon-btn px-6 py-3 mt-4" data-testid="surpass-save">
            {savingSurpass ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Settings</>}
          </button>
        </div>
      )}
    </div>
  );
}
